import "@src/pages/businessAnalysis/businessAnalysis.css";
import "@src/pages/payroll/payroll.css";
import "@src/pages/site/duplicateSiteAlert.css";
import { useState } from "react";
import dayjs from "dayjs";
import { Button, Modal } from "antd";
import { useMutation, useQuery } from "@apollo/client";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { CloseOutlined, WarningOutlined } from "@ant-design/icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClose } from "@fortawesome/free-solid-svg-icons";
import { payrollMinimumWageQuery } from "@src/util/query/payroll.query";
import { calculatePayBillMutation } from "@src/util/mutation/calculatePayBill.mutation";
import { setNotification } from "@src/middleware/redux/reducers/notification.reducer";
import { formatMoney } from "@src/util/currency/currency";
import { InfoHint } from "@src/components/infoHint/infoHint";
import { EditDutyModal } from "@src/pages/schedule/editDutyModal/editDuty.modal";
import Spinner from "@src/components/spinner";
import { useTenantFeatureFlags } from "@src/hooks/useTenantFeatureFlags";

const money = (v) => formatMoney(v, "GBP");
const day = (v) => dayjs(v).format("DD/MM/YYYY");
const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

/**
 * Who is paid below the National Minimum Wage / National Living Wage on duties last month or
 * this month - the Payroll page's minimum wage warning, raised before payroll is run. For the
 * whole company, or one employee. Only for companies with the Payroll module, and people with
 * Reports access (the server also refuses a login limited to part of the company, except for
 * one employee inside its scope).
 */
export const useMinimumWage = (employeeId = null, skip = false) => {
    const permission = useSelector((state) => state.userAuthReducer?.authenticatedUserDetail?.permission);
    const { payrollEnabled } = useTenantFeatureFlags();
    const canSee = !skip && payrollEnabled && permission?.Reports?.access !== false;
    const { data, refetch } = useQuery(payrollMinimumWageQuery, { variables: { employeeId }, fetchPolicy: "network-only", skip: !canSee });
    const result = canSee ? data?.payrollMinimumWage?.response : null;
    return { canSee, result, refetch, count: result?.employees?.length || 0 };
};

const bannerText = (count, result) => (
    <>
        <b>{count}</b> employee{count === 1 ? " is" : "s are"} paid below the National Minimum Wage on duties from {day(result.from)} to {day(result.to)}
        {result.shortfall > 0 && <> — about <b style={{ color: "#dc3545" }}>{money(result.shortfall)}</b> short</>}
    </>
);

// Dashboard: the same look as the data quality banner above it
export const MinimumWageNotice = () => {
    const { canSee, result, refetch, count } = useMinimumWage();
    const [open, setOpen] = useState(false);
    const [dismissed, setDismissed] = useState(false);

    return (
        <>
            {count > 0 && !dismissed && (
                <div className="data-health-banner" onClick={() => setOpen(true)}>
                    <WarningOutlined className="data-health-banner-icon" style={{ fontSize: "16px", color: "#dc3545", flexShrink: 0 }} />
                    <span className="data-health-banner-text">{bannerText(count, result)}</span>
                    <span className="data-health-banner-action">View Employees</span>
                    <button className="data-health-banner-close" onClick={(e) => { e.stopPropagation(); setDismissed(true); }} title="Dismiss for now">
                        <CloseOutlined style={{ fontSize: "12px" }} />
                    </button>
                </div>
            )}
            {canSee && <MinimumWageModal visible={open} close={() => setOpen(false)} result={result} refetch={refetch} />}
        </>
    );
};

// Employees list: the same look as its possible-duplicates alert
export const MinimumWageAlert = () => {
    const { canSee, result, refetch, count } = useMinimumWage();
    const [open, setOpen] = useState(false);
    const [dismissed, setDismissed] = useState(false);

    return (
        <>
            {count > 0 && !dismissed && (
                <div className="duplicate-alert">
                    <div className="duplicate-alert-icon"><WarningOutlined /></div>
                    <div className="duplicate-alert-body">
                        <div className="duplicate-alert-title">{plural(count, "employee is", "employees are")} paid below the National Minimum Wage</div>
                        <div className="duplicate-alert-desc">{bannerText(count, result)}. Check their pay rates before payroll.</div>
                    </div>
                    <button className="duplicate-alert-action" onClick={() => setOpen(true)}>Review</button>
                    <button className="duplicate-alert-dismiss" aria-label="Dismiss" onClick={() => setDismissed(true)}>
                        <FontAwesomeIcon icon={faClose} />
                    </button>
                </div>
            )}
            {canSee && <MinimumWageModal visible={open} close={() => setOpen(false)} result={result} refetch={refetch} />}
        </>
    );
};

export const MinimumWageModal = ({ visible, close, result, refetch }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [calculatePayBill] = useMutation(calculatePayBillMutation);
    const [busy, setBusy] = useState(null);
    const [editingDuty, setEditingDuty] = useState(null);
    const employees = result?.employees || [];

    const recalculate = async (key, duties) => {
        setBusy(key);
        try {
            await calculatePayBill({ variables: { input: { scheduleIds: duties.map((d) => d.scheduleId) } } });
            dispatch(setNotification({ error: false, notificationState: true, message: `Pay calculated again for ${plural(duties.length, "duty", "duties")}` }));
            await refetch();
        } catch (err) {
            dispatch(setNotification({ error: true, notificationState: true, message: err?.message || "Pay wasn't calculated" }));
        } finally {
            setBusy(null);
        }
    };
    const openProfile = (e) => { close(); navigate(`/user/employee-detail/${e.employeeId}`); };
    const openDuty = (d) => setEditingDuty({ jobId: d.scheduleId, _id: d.siteId, sitename: d.site, selectedarea: {}, isCompleted: d.completed, isOnDuty: false });

    return (
        <>
            <Modal
                open={visible}
                width={820}
                closable={false}
                onCancel={close}
                className="payroll-detail-modal"
                footer={
                    <div className="drawer-footer">
                        <button className="drawer-filled-btn" onClick={() => { close(); navigate(`/user/payroll?from=${result?.from}&to=${dayjs(result?.from).endOf("month").format("YYYY-MM-DD")}`); }}>Open Payroll</button>
                        <button className="drawer-outlined-btn" onClick={close}>Close</button>
                    </div>
                }
            >
                <div className="modal-header-title">
                    <span>
                        Pay Below the Minimum Wage
                        <InfoHint title="How this is checked" lines={[
                            "The National Minimum Wage Act 1998 and the National Minimum Wage Regulations 2015 set the lowest hourly pay for workers, by age: from 1 April 2026 £12.71 (21 and over, the National Living Wage), £10.85 (18 to 20), £8.00 (under 18).",
                            "Each duty's basic hourly rate is checked against the rate for the employee's age on the day worked. Overtime and premiums don't count towards the minimum wage, so they aren't used to lift it.",
                            "With no date of birth on the profile, the 21-and-over rate is used.",
                            "The law looks at the average over the whole pay period, so a flagged rate might still be legal if other hours that period paid more. Your payroll software makes the final check.",
                            "Employed staff only: agency staff are paid by their supplier and self-employed staff aren't covered.",
                        ]} />
                    </span>
                    <span onClick={close}><FontAwesomeIcon className="close" icon={faClose} /></span>
                </div>
                <div className="modal-body">
                    {!employees.length ? (
                        <div className="text">Nobody is paid below the minimum wage on duties from {day(result?.from)} to {day(result?.to)}.</div>
                    ) : (
                        <div className="payroll-detail">
                            <div className="text">
                                {plural(employees.length, "employee is", "employees are")} paid below the legal minimum on duties from {day(result.from)} to {day(result.to)}
                                {result.shortfall > 0 ? `, about ${money(result.shortfall)} short in total` : ""}. Change the rate where it comes from (shown on each duty), then calculate pay again.
                            </div>
                            {employees.map((e) => (
                                <div key={e.employeeId} className="ba-callout error payroll-issue">
                                    <WarningOutlined className="ba-callout-icon" />
                                    <div className="payroll-issue-body">
                                        <div className="payroll-issue-head">
                                            <div>
                                                <button className="ba-link-btn payroll-name" onClick={() => openProfile(e)}>{e.name}</button>
                                                <span className="inv-sub"> · {[e.employmentType || "Employed", e.payrollNumber ? `Payroll No. ${e.payrollNumber}` : null].filter(Boolean).join(" · ")}</span>
                                            </div>
                                        </div>
                                        {e.items.map((item, i) => {
                                            const key = `${e.employeeId}:${i}`;
                                            return (
                                                <div key={i} className="payroll-wage-item">
                                                    <div className="payroll-issue-head">
                                                        <div>
                                                            <div className="ba-callout-title">
                                                                {money(item.rate)} an hour{item.kind === "mobile" ? " on average on mobile jobs" : ""} - the {item.label} is {money(item.minimum)}
                                                            </div>
                                                            <div className="inv-sub">
                                                                {plural(item.lines, "pay line", "pay lines")} · {Number(item.hours || 0).toFixed(2)} hours · {money(item.shortfall)} short
                                                                {item.noDateOfBirth ? " · no date of birth, so the 21-and-over rate is used" : ""}
                                                            </div>
                                                        </div>
                                                        <div className="payroll-fix-inline">
                                                            <button className="ba-link-btn" onClick={() => openProfile(e)}>Open profile</button>
                                                            {item.duties?.length > 0 && (
                                                                <Button className="sm-btn" style={{ height: 36 }} disabled={busy === key} onClick={() => recalculate(key, item.duties)}>
                                                                    {busy === key ? <Spinner /> : `Calculate pay again (${item.duties.length})`}
                                                                </Button>
                                                            )}
                                                        </div>
                                                    </div>
                                                    {item.duties?.length > 0 && (
                                                        <div className="ba-rate-list payroll-issue-duties">
                                                            {item.duties.map((d) => (
                                                                <div className="ba-rate-item" key={d.scheduleId}>
                                                                    <div>
                                                                        <div className="ba-rate-item-name">{dayjs(d.date).format("ddd DD/MM/YYYY")} · {d.start}{d.end ? ` - ${d.end}` : ""}</div>
                                                                        <div className="ba-rate-item-meta">
                                                                            {(d.mobileServiceId
                                                                                ? [d.site, d.dutyType, d.callout ? "Callout" : "Mobile job", `${Number(d.hours || 0).toFixed(2)}h on the job`, money(d.amount)]
                                                                                : [d.site, d.dutyType, d.rateSource ? `Rate from: ${d.rateSource.replace(/^pay rate from /i, "")}` : null]).filter(Boolean).join(" · ")}
                                                                        </div>
                                                                    </div>
                                                                    {d.scheduleId && <button className="ba-link-btn" onClick={() => openDuty(d)}>Open duty</button>}
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </Modal>
            {/* after the list, so it opens on top of it */}
            {editingDuty && <EditDutyModal editData={editingDuty} close={() => setEditingDuty(null)} refetch={refetch} />}
        </>
    );
};
